import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PackageCheck, Truck, MapPin, CheckCircle2, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, Empty } from "@/components/common";

const NICE = {
  CREATED: "Order created", LABEL_CREATED: "Label created", READY_TO_SHIP: "Ready to ship",
  IN_TRANSIT: "In transit", OUT_FOR_DELIVERY: "Out for delivery", DELIVERED: "Delivered",
};

export default function Track() {
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get(`/shipping/track/${trackingNumber}`).then(setData).catch(() => setData(null));
  }, [trackingNumber]);

  if (data === undefined) return <Loading />;
  if (!data) return <Empty icon={Truck} title="Tracking not found" sub="We couldn't find a shipment with that tracking number." />;

  const flow = data.flow || [];
  const currentIdx = flow.indexOf(data.status);

  return (
    <div className="fade-up" style={{ maxWidth: 640, margin: "0 auto" }}>

      <div className="ss-card" style={{ padding: 24 }} data-testid="track-card">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="stat-ic" style={{ width: 52, height: 52 }}><PackageCheck size={24} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>{data.item_title || "Your shipment"}</div>
            <div className="ss-muted" style={{ fontSize: 13, marginTop: 2 }}>
              {data.carrier ? `${data.carrier}${data.service ? ` · ${data.service}` : ""}` : "Carrier"}
            </div>
          </div>
          <span className={`ss-pill ${data.status === "DELIVERED" ? "pill--delivered" : "pill--shipped"}`} data-testid="track-status">{NICE[data.status] || data.status}</span>
        </div>

        {data.estimated_delivery && (
          <div style={{ marginTop: 10, fontWeight: 800, fontSize: 15 }} data-testid="track-eta">
            {data.delivered
              ? `Delivered on ${new Date(data.estimated_delivery).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`
              : `Arriving by ${new Date(data.estimated_delivery).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`}
          </div>
        )}

        <div style={{ marginTop: 12, padding: "12px 14px", background: "var(--gold-soft)", borderRadius: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Truck size={16} style={{ color: "var(--gold-2)" }} />
          <div style={{ fontSize: 14, flex: 1 }}>
            <span className="ss-muted">Tracking number</span>{" "}
            <b style={{ letterSpacing: 1 }} data-testid="track-number">{data.tracking_number}</b>
          </div>
          {data.tracking_url && (
            <a href={data.tracking_url} target="_blank" rel="noopener noreferrer" className="ss-btn ss-btn-gold ss-btn-sm" data-testid="track-external-link">
              <Truck size={14} /> Track on {data.carrier || "carrier"} site
            </a>
          )}
        </div>

        {data.manual_tracking ? (
          <div style={{ marginTop: 16, fontSize: 13 }} className="ss-muted" data-testid="track-manual-note">
            This order ships with {data.carrier || "the seller's carrier"}. Live status updates are available on the carrier's website above.
          </div>
        ) : (
        <>
        {/* Progress flow */}
        <div style={{ marginTop: 22 }}>
          {flow.map((st, i) => {
            const done = i <= currentIdx;
            return (
              <div key={st} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  {done ? <CheckCircle2 size={20} style={{ color: "var(--success)" }} /> : <Circle size={20} style={{ color: "var(--line)" }} />}
                  {i < flow.length - 1 && <div style={{ width: 2, height: 26, background: i < currentIdx ? "var(--success)" : "var(--line)" }} />}
                </div>
                <div style={{ paddingBottom: 8 }}>
                  <div style={{ fontWeight: done ? 800 : 600, color: done ? "var(--text)" : "var(--muted)" }}>{NICE[st] || st}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed events */}
        {data.events?.length > 0 && (
          <div style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 10, fontSize: 14 }}>Tracking history</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[...data.events].reverse().map((e, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14 }}>
                  <MapPin size={15} className="ss-muted" style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 700 }}>{e.description || NICE[e.status] || e.status}</div>
                    <div className="ss-muted" style={{ fontSize: 12 }}>{e.location || ""}{e.location && e.timestamp ? " · " : ""}{e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
